import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  ConsultaDaBiblioteca,
  ReportBrief,
  ReportJob,
  ReportPreferences,
  ReportsScope,
} from '../types';
import { gatewayDoProduto, RelatoriosLexApiError } from './services';

/** Recusas do core cuja mensagem já fala com a pessoa; as outras viram a mensagem da operação. */
const RECUSAS_HUMANIZADAS = new Set([
  'RELATORIO_LEX_ATIVO_EXISTENTE',
  'RELATORIO_LEX_LIMITE_DO_ESCRITORIO',
]);

export const interpretReport = createAsyncThunk(
  'relatoriosLex/interpret',
  (args: { scope: ReportsScope; text: string; currentBrief?: ReportBrief }) =>
    gatewayDoProduto(args.scope.produto).interpret(
      args.scope.workspaceId,
      args.text,
      args.currentBrief,
    ),
);
export const loadReports = createAsyncThunk('relatoriosLex/load', async (scope: ReportsScope) => {
  const gateway = gatewayDoProduto(scope.produto);
  // A preferência só pré-preenche o formato: se a leitura falha, a biblioteca chega mesmo assim.
  // `null` aqui é "não lida" (o gateway sempre devolve uma), e o reducer mantém a que já havia.
  const [page, preferences] = await Promise.all([
    gateway.list(scope.workspaceId, {}),
    gateway.readPreferences(scope.workspaceId).catch(() => null),
  ]);
  return { page, preferences };
});
/** Página da biblioteca: sem cursor é a primeira da consulta; com cursor, a seguinte. */
export const listReports = createAsyncThunk(
  'relatoriosLex/list',
  (args: { scope: ReportsScope; query: ConsultaDaBiblioteca; cursor?: string }) =>
    gatewayDoProduto(args.scope.produto).list(args.scope.workspaceId, {
      ...args.query,
      cursor: args.cursor,
    }),
);
export const createReport = createAsyncThunk<
  ReportJob,
  { scope: ReportsScope; brief: ReportBrief; key: string },
  { rejectValue: string }
>('relatoriosLex/create', async (args, { rejectWithValue }) => {
  try {
    return await gatewayDoProduto(args.scope.produto).create(
      args.scope.workspaceId,
      args.brief,
      args.key,
    );
  } catch (error) {
    if (error instanceof RelatoriosLexApiError && error.code && RECUSAS_HUMANIZADAS.has(error.code))
      return rejectWithValue(error.message);
    throw error;
  }
});
export const pollReport = createAsyncThunk(
  'relatoriosLex/poll',
  (args: { scope: ReportsScope; id: string }) =>
    gatewayDoProduto(args.scope.produto).get(args.scope.workspaceId, args.id),
);
export const openReport = createAsyncThunk(
  'relatoriosLex/open',
  (args: { scope: ReportsScope; id: string }) =>
    gatewayDoProduto(args.scope.produto).get(args.scope.workspaceId, args.id),
);
export const cancelReport = createAsyncThunk(
  'relatoriosLex/cancel',
  (args: { scope: ReportsScope; id: string }) =>
    gatewayDoProduto(args.scope.produto).cancel(args.scope.workspaceId, args.id),
);
export const savePreferences = createAsyncThunk(
  'relatoriosLex/preferences',
  (args: { scope: ReportsScope; preferences: ReportPreferences }) =>
    gatewayDoProduto(args.scope.produto).savePreferences(args.scope.workspaceId, args.preferences),
);
